import React from "react";
import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ISortList {
    items: Array<any>,
    renderItem: (item: any, i: number) => JSX.Element,
    onSort: (list: any) => void,
}

interface ISortableItem {
    id: string;
    children: React.ReactNode;
}

const SortableItem: React.FunctionComponent<ISortableItem> = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
        </div>
    );
};

export const SortableList: React.FunctionComponent<ISortList> = (props) => {
    const { items, renderItem, onSort } = props;

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = items.findIndex((_, i) => `row${i}` === active.id);
        const newIndex = items.findIndex((_, i) => `row${i}` === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        onSort(arrayMove(items, oldIndex, newIndex));
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((_, i) => `row${i}`)} strategy={verticalListSortingStrategy}>
                <div className="main-container">
                    {items?.map((s, i) => (
                        <SortableItem key={`row${i}`} id={`row${i}`}>
                            {renderItem(s, i)}
                        </SortableItem>
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};
